"use client";

import { useState, useEffect, useCallback } from "react";
import { Card, Form, Button, Modal } from "react-bootstrap";
import SyncLoader from "react-spinners/SyncLoader";
import { showLoading } from "react-global-loading";
import { toast } from "react-toastify";

import PaperDetail from "../../Paper/Detail";

import * as myApi from "../../Api";

// Function to convert text to Title Case
const toTitleCase = (str) => {
  if (!str) return str;
  return str.toLowerCase().split(' ').map(word => {
    if (word.length > 0) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    }
    return word;
  }).join(' ');
};

const PaperFullTable = () => {
  const [userRole, setUserRole] = useState("penulis");
  const [incomingData, setIncomingData] = useState([{}]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchType] = useState("search");
  const [filter, setFilter] = useState("");
  const [keyword, setKeyword] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState({});
  const [statusFilter, setStatusFilter] = useState("");
  const [paperTypeFilter, setPaperTypeFilter] = useState("");
  const [selectedPapers, setSelectedPapers] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loaderStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      fetchData(page);
    }
  };

  const fetchData = useCallback((page=1, count=10) => {

    setLoading(true);

    let option = {
      search_type: searchType,
      user_role: myApi.getUser().role,
      filter: filter,
      keyword: keyword,
      page: page,
      count: count,
      status_filter: statusFilter,
      paper_type_filter: paperTypeFilter
    };

    setTimeout(() => {
      myApi.post("/paper/search/", option).then((res) => {
        let result = res.data.data;
        setIncomingData(result.data);
        setCurrentPage(result.paginate.page);
        setItemsPerPage(result.count);
        setTotalItems(result.total);
        setTotalPages(result.paginate.last);
        
        // Calculate stats
        const pending = result.data.filter(p => p.paper_status === "-").length;
        const approved = result.data.filter(p => p.paper_status === "lolos").length;
        const rejected = result.data.filter(p => p.paper_status === "gagal").length;
        setStats({ pending, approved, rejected });

        setLoading(false);
      }).catch(() => {
        setLoading(false);
      });
    }, 500);
  }, [searchType, filter, keyword, statusFilter, paperTypeFilter]);

  const handleDownloadModal = (show=true, paper) => {
    if(show) {
      setShowModal(true);
      setSelectedPaper(paper);
    } else {
      setShowModal(false);
      setSelectedPaper({});
    }
  }

  const handleDirectDownload = (paper) => {
    if (paper.paper_filePath_1) {
      const baseUrl = window.env?.API_URL || process.env.REACT_APP_API_URL || "http://localhost:3001";
      const link = document.createElement('a');
      link.href = `${baseUrl}/${paper.paper_filePath_1}`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.download = paper.paper_fileName_1 || '';
      link.click();
      toast("Downloading paper...", { type: "info" });
    } else {
      toast("Paper file not available", { type: "warning" });
    }
  }

  const handleSearch = (e) => {
    const filterValue = document.getElementById("filterBy")?.value || "";
    const keywordValue = document.getElementById("keyword")?.value || "";
    
    setFilter(filterValue);
    setKeyword(keywordValue);
    
    // Debounce search for keyword input
    if (e.target.id === "keyword") {
      setTimeout(() => {
        fetchData(1);
      }, 500);
    } else {
      // Immediate search when changing filter type
      fetchData(1);
    }
  }

  const handleClearSearch = () => {
    setFilter("");
    setKeyword("");
    if (document.getElementById("keyword")) {
      document.getElementById("keyword").value = "";
    }
    if (document.getElementById("filterBy")) {
      document.getElementById("filterBy").value = "";
    }
    fetchData(1);
  }

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
    fetchData(1);
  }

  const handlePaperTypeFilterChange = (e) => {
    setPaperTypeFilter(e.target.value);
    setCurrentPage(1);
    fetchData(1);
  }

  const toggleSelectPaper = (paperCode) => {
    if (selectedPapers.includes(paperCode)) {
      setSelectedPapers(selectedPapers.filter(code => code !== paperCode));
    } else {
      setSelectedPapers([...selectedPapers, paperCode]);
    }
  }

  const handleBulkApproval = (is_approved) => {
    if (selectedPapers.length === 0) {
      toast("Please select at least one paper", { type: "warning" });
      return;
    }

    const confirmText = `Are you sure to ${is_approved ? "approve" : "reject"} ${selectedPapers.length} selected paper(s)?`;
    if (!window.confirm(confirmText)) {
      return;
    }

    showLoading(true);
    const approvalData = {
      paper_codes: selectedPapers,
      paper_status: is_approved ? "lolos" : "gagal"
    };

    myApi.post("/paper/bulk-approval/", approvalData).then((res) => {
      showLoading(false);
      toast(`${selectedPapers.length} paper(s) successfully ${is_approved ? "approved" : "rejected"}`, { type: "success" });
      setSelectedPapers([]);
      fetchData();
    }).catch(() => {
      showLoading(false);
    });
  }

  const handleApproval = (id, is_approved=true) => {
    let confirmText = `Are you sure to ${is_approved ? "approve" : "reject"} this paper?`

    if (is_approved === null) {
      confirmText = `Are you sure to change approval of this paper?`
    }

    // show confirmation dialog
    if (!window.confirm(confirmText)) {
      return;
    }

    let approvalData = {
      paper_code: id,
      paper_status: is_approved ? "lolos" : "gagal"
    }

    if (is_approved === null) {
      approvalData.paper_status = "-"
    }

    showLoading(true);
    myApi.post("/paper/approval/", approvalData).then((res) => {
      showLoading(false);
      toast("Paper berhasil disetujui", { type: "success" });
      fetchData();
    }).catch(() => {
      showLoading(false);
    });
  }

  const handlePaperSubmit = async(id, email) => {
    if (submitting) return; // Prevent double submission
    
    try {
      const response = await myApi.get('/deadline');
      if (response.data.result) {
        const deadlineInfo = response.data.result;
        const dateNow = new Date();
        const dateLimit = new Date(deadlineInfo.general_deadline);

        if (dateNow > dateLimit) {
          toast("Pendaftaran paper telah ditutup", { type: "error" });
          return;
        } else {
          setSubmitting(true);
          showLoading(true);
          await myApi.post("/submitPaper", {data_papercode: id, data_email: email});
          showLoading(false);
          setSubmitting(false);
          fetchData();
        }
      }
    } catch (error) {
      showLoading(false);
      setSubmitting(false);
    }
  }

  const handlePaperAdd = async() => {
    try {
      const response = await myApi.get('/deadline');
      if (response.data.result) {
        const deadlineInfo = response.data.result;
        const dateNow = new Date();
        const dateLimit = new Date(deadlineInfo.general_deadline);

        if (dateNow > dateLimit) {
          toast("Pendaftaran paper telah ditutup", { type: "error" });
          return;
        } else {
          window.location.href = "/paper/add/";
        }
      }
    } catch (error) {
      toast("Error checking deadline: " + error.message, { type: "error" });
    }
  }

  useEffect(() => {
    let user = myApi.getUser();
    setUserRole(user.role);
    fetchData();
  }, [fetchData]);

  return (
    <>
      <Card className="bg-white border-0 rounded-3 mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          <Card.Title className="mb-0">Paper List</Card.Title>
          <Button className={ userRole === 'admin' ? 'd-none' : '' } variant="primary" onClick={ handlePaperAdd }>
            <i className="material-symbols-outlined fs-14">book</i> Add Paper
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {/* Stats Cards for Admin */}
          {userRole === 'admin' && (
            <div className="d-flex gap-2 mb-3 p-2 bg-light rounded-3">
              <div className="flex-fill text-center p-2 bg-warning bg-opacity-15 rounded-3 border border-warning border-opacity-25">
                <h4 className="mb-0 text-warning-emphasis fw-bold fs-3">{stats.pending}</h4>
                <small className="text-dark fw-medium">Pending</small>
              </div>
              <div className="flex-fill text-center p-2 bg-success bg-opacity-15 rounded-3 border border-success border-opacity-25">
                <h4 className="mb-0 text-success-emphasis fw-bold fs-3">{stats.approved}</h4>
                <small className="text-dark fw-medium">Approved</small>
              </div>
              <div className="flex-fill text-center p-2 bg-danger bg-opacity-15 rounded-3 border border-danger border-opacity-25">
                <h4 className="mb-0 text-danger-emphasis fw-bold fs-3">{stats.rejected}</h4>
                <small className="text-dark fw-medium">Rejected</small>
              </div>
              <div className="flex-fill text-center p-2 bg-primary bg-opacity-15 rounded-3 border border-primary border-opacity-25">
                <h4 className="mb-0 text-primary-emphasis fw-bold fs-3">{totalItems}</h4>
                <small className="text-dark fw-medium">Total</small>
              </div>
            </div>
          )}

          {/* Bulk Actions for Admin */}
          {userRole === 'admin' && selectedPapers.length > 0 && (
            <div className="p-3 mb-3 bg-primary bg-opacity-10 rounded-3 d-flex align-items-center justify-content-between">
              <span className="fw-semibold">
                <i className="material-symbols-outlined fs-5 align-middle me-2">checklist</i>
                {selectedPapers.length} paper(s) selected
              </span>
              <div className="d-flex gap-2">
                <Button variant="outline-secondary" size="sm" onClick={() => setSelectedPapers([])}>
                  <i className="material-symbols-outlined fs-6">close</i> Clear
                </Button>
                <Button variant="success" size="sm" onClick={() => handleBulkApproval(true)}>
                  <i className="material-symbols-outlined fs-6">check</i> Approve All
                </Button>
                <Button variant="danger" size="sm" onClick={() => handleBulkApproval(false)}>
                  <i className="material-symbols-outlined fs-6">close</i> Reject All
                </Button>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="p-0 pb-4 pt-4 border-bottom mb-4 bg-white">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <Form.Group className="flex-grow-1 d-flex align-items-center gap-2">
                <Form.Select 
                  onKeyUpCapture={handleSearch}
                  id="filterBy"
                  className="month-select form-control p-2 h-auto border-1"
                  style={{ width: "150px", minWidth: "150px" }}
                  disabled={myApi.getUser().role !== "admin"}
                >
                  <option value={""} defaultValue="">Search By</option>
                  <option value={"kodepaper"}>Kode Paper</option>
                  <option value={"nama"}>Nama Penulis</option>
                  <option value={"judul"}>Judul Paper</option>
                </Form.Select>
                <Form.Control
                  id="keyword"
                  type="search"
                  placeholder="Search papers..."
                  className="border-1 p-2 h-auto"
                  style={{ flex: "1", minWidth: "200px" }}
                  onKeyUpCapture={handleSearch}
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                />
                {keyword && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleClearSearch}
                    className="ms-2"
                    title="Clear Search"
                  >
                    <i className="material-symbols-outlined fs-6">close</i>
                  </Button>
                )}
                {userRole === 'admin' && (
                  <>
                    <Form.Select 
                      value={statusFilter} 
                      onChange={handleStatusFilterChange}
                      className="form-control p-2 h-auto border-1"
                      style={{ width: "140px", minWidth: "140px" }}
                    >
                      <option value="">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </Form.Select>
                    <Form.Select 
                      value={paperTypeFilter} 
                      onChange={handlePaperTypeFilterChange}
                      className="form-control p-2 h-auto border-1"
                      style={{ width: "140px", minWidth: "140px" }}
                    >
                      <option value="">All Types</option>
                      <option value="PM">PM (Mahasiswa)</option>
                      <option value="PU">PU (Umum)</option>
                    </Form.Select>
                  </>
                )}
              </Form.Group>
            </div>
          </div>

          <div className="default-table-area">
            <SyncLoader className="p-5"
              color={"#AD63F6"}
              size={10}
              aria-label="Loading Spinner"
              data-testid="loader"
              loading={loading}
              cssOverride={loaderStyle}
            />
            <div style={{ display: loading ? "none" : "block" }}>
              <div className="paper-list-container">
                {/* Header */}
                <div className="paper-list-header">
                  {userRole === 'admin' && <div className="paper-col-checkbox"></div>}
                  <div className="paper-col-code">Code</div>
                  <div className="paper-col-details">Paper Details</div>
                  <div className="paper-col-author">Author</div>
                </div>

                {/* Paper Items */}
                {totalItems === 0 && (
                  <div className="paper-list-empty">
                    <i className="material-symbols-outlined fs-1 text-muted mb-3">inbox</i>
                    <p className="text-muted">No papers found</p>
                  </div>
                )}

                {totalItems !== 0 && incomingData.map((value, i) => (
                  <div key={value.paper_code || i} className={`paper-list-item ${selectedPapers.includes(value.paper_code) ? 'table-selected' : ''}`}>
                    {/* Row 1: Title & Author */}
                    <div className="paper-item-row paper-item-row-1">
                      {userRole === 'admin' && (
                        <div className="paper-col-checkbox">
                          <Form.Check
                            type="checkbox"
                            checked={selectedPapers.includes(value.paper_code)}
                            onChange={() => toggleSelectPaper(value.paper_code)}
                            disabled={value.paper_status !== "-"}
                          />
                        </div>
                      )}
                        <div className="paper-col-details" style={{ flex: '1', minWidth: 0 }}>
                          <div className="paper-title fw-bold fs-16 mb-1">{toTitleCase(value.title)}</div>
                          <small className="text-muted d-block text-truncate">{value.name_1 || value.email_1}</small>
                        </div>
                      <div className="paper-col-code" style={{ flexShrink: 0 }}>
                        {value.submit_status === 'submit' && (
                            <span className="badge bg-secondary">{value.paper_code}</span>
                        )}
                      </div>
                    </div>

                    {/* Row 2: Sub Theme */}
                    <div className="paper-item-row paper-item-row-2">
                      <div className="paper-col-details-full">
                        <div className="d-flex align-items-center gap-2">
                          <i className="material-symbols-outlined fs-5 text-muted">topic</i>
                          <small className="text-muted fw-medium">Sub Theme:</small>
                          <span className="badge bg-success bg-opacity-75">{value.sub_theme}</span>
                        </div>
                      </div>
                    </div>

                    {/* Row 3: Type & Participation */}
                    <div className="paper-item-row paper-item-row-3">
                      <div className="paper-col-details-full">
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                          <div className="d-flex align-items-center gap-2">
                            <i className="material-symbols-outlined fs-5 text-muted">category</i>
                            <small className="text-muted fw-medium">Type:</small>
                            <span className="badge bg-info bg-opacity-75">{value.paper_type}</span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <i className="material-symbols-outlined fs-5 text-muted">groups</i>
                            <small className="text-muted fw-medium">Participation:</small>
                            <span className="badge bg-secondary bg-opacity-75">{value.participation_type}</span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <small className="text-muted fw-medium">Category:</small>
                            <span className="text-muted">{value.category}</span>
                          </div>
                          {value.paper_type === "PM" && value.category === "Mahasiswa" && (
                            <span className="badge bg-info bg-opacity-75">Student Paper</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 4: Status & Submitted Date */}
                    <div className="paper-item-row paper-item-row-4">
                      <div className="paper-col-details-full">
                        <div className="d-flex align-items-center gap-4 flex-wrap">
                          <div className="d-flex align-items-center gap-2">
                            <i className="material-symbols-outlined fs-5 text-muted">assignment_turned_in</i>
                            <small className="text-muted fw-medium">Status:</small>
                            {value.submit_status !== 'submit' && (
                              <span className="badge bg-secondary">Not Submitted</span>
                            )}
                            {value.submit_status === 'submit' && value.paper_status === "lolos" && (
                              <span className="badge bg-success">Approved</span>
                            )}
                            {value.submit_status === 'submit' && value.paper_status === "gagal" && (
                              <span className="badge bg-danger">Rejected</span>
                            )}
                            {value.submit_status === 'submit' && value.paper_status === "-" && (
                              <span className="badge bg-secondary">Waiting for Approval</span>
                            )}
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <i className="material-symbols-outlined fs-5 text-muted">schedule</i>
                            <small className="text-muted fw-medium">Submitted:</small>
                            <span className="text-muted">{value.submit_status === 'submit' && value.submission_date ? value.submission_date : '-'}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Row 5: Actions */}
                    <div className="paper-item-row paper-item-row-5">
                      <div className="paper-col-details-full">
                        <div className="d-flex align-items-center gap-2">
                          <i className="material-symbols-outlined fs-5 text-muted">settings</i>
                          <small className="text-muted fw-medium">Actions:</small>
                          <div className="d-flex gap-2 ms-2 flex-wrap">
                            {/* View Detail Button */}
                            <Button
                              variant="warning"
                              size="sm"
                              className="action-btn"
                              onClick={() => handleDownloadModal(true, value)}
                              title="View Paper Details"
                            >
                              <i className="material-symbols-outlined fs-5">visibility</i>
                              <span className="ms-1">View</span>
                            </Button>
                            
                            {/* Download Button */}
                            <Button
                              variant="dark"
                              size="sm"
                              className="action-btn"
                              onClick={() => handleDirectDownload(value)}
                              title="Download Paper"
                            >
                              <i className="material-symbols-outlined fs-5">download</i>
                              <span className="ms-1">Download</span>
                            </Button>
                            
                            {/* Admin Actions - Approve/Reject */}
                            {userRole === 'admin' && value.paper_status === "-" && value.submit_status === 'submit' && (
                              <>
                                <Button
                                  variant="success"
                                  size="sm"
                                  className="action-btn"
                                  onClick={() => handleApproval(value.paper_code, true)}
                                  title="Approve Paper"
                                >
                                  <i className="material-symbols-outlined fs-5">check_circle</i>
                                  <span className="ms-1">Approve</span>
                                </Button>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  className="action-btn"
                                  onClick={() => handleApproval(value.paper_code, false)}
                                  title="Reject Paper"
                                >
                                  <i className="material-symbols-outlined fs-5">cancel</i>
                                  <span className="ms-1">Reject</span>
                                </Button>
                              </>
                            )}
                            
                            {/* User Submit Button */}
                            {userRole !== 'admin' && value.paper_status === "-" && value.submit_status !== 'submit' && (
                              <Button
                                variant="info"
                                size="sm"
                                className="action-btn"
                                onClick={() => handlePaperSubmit(value.paper_code, value.email_1)}
                                disabled={submitting}
                                title="Submit Paper for Review"
                              >
                                <i className="material-symbols-outlined fs-5">send</i>
                                <span className="ms-1">{submitting ? 'Submitting...' : 'Submit'}</span>
                              </Button>
                            )}

                            {/* User Edit Button - only show for non-submitted papers */}
                            {userRole !== 'admin' && value.submit_status !== 'submit' && (
                              <Button
                                variant="primary"
                                size="sm"
                                className="action-btn"
                                onClick={() => window.location.href = `/paper/edit/?code=${value.paper_code}`}
                                title="Edit Paper"
                              >
                                <i className="material-symbols-outlined fs-5">edit</i>
                                <span className="ms-1">Edit</span>
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 pt-lg-4 sticky-bottom bg-white">
              <div className="d-flex justify-content-center justify-content-sm-between align-items-center text-center flex-wrap gap-2">
                <span className="fs-12 fw-medium">
                  Showing {(currentPage - 1) * itemsPerPage + 1} -{" "}
                  {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                  {totalItems}
                </span>

                <nav aria-label="Page navigation example">
                  <ul className="pagination mb-0 justify-content-center">
                    <li
                      className={`page-item ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link icon"
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        <span className="material-symbols-outlined">
                          keyboard_arrow_left
                        </span>
                      </button>
                    </li>
                    {/* {Array.from({ length: totalPages }, (_, index) => (
                      <li key={index} className="page-item">
                        <button
                          className={`page-link ${
                            currentPage === index + 1 ? "active" : ""
                          }`}
                          onClick={() => handlePageChange(index + 1)}
                        >
                          {index + 1}
                        </button>
                      </li>
                    ))} */}
                    <li className={"page-item " + (totalPages > 1 ? "" : "d-none")}>
                      <select className="form-select" value={currentPage} onChange={(e) => handlePageChange(e.target.value)}>
                        {Array.from({ length: totalPages }, (_, index) => (
                          <option key={index + 1} value={index + 1}>{index + 1}</option>
                        ))}
                      </select>
                    </li>
                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link icon"
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        <span className="material-symbols-outlined">
                          keyboard_arrow_right
                        </span>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="xl"
        fullscreen="xl-down"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        scrollable
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title id="contained-modal-title-vcenter" className="d-flex align-items-center gap-2">
            <i className="material-symbols-outlined text-primary">description</i>
            <span>Paper Details: {selectedPaper.submit_status === 'submit' ? selectedPaper.paper_code : 'Not Submitted Yet'}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="scrollable">
          <PaperDetail paper={selectedPaper} />
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="outline-secondary" onClick={() => setShowModal(false)}>
            <i className="material-symbols-outlined fs-6 align-middle me-1">close</i>Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PaperFullTable;
